import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import './NavigationStyle.css';

import logo from '../assets/images/iccvt-new-logo.png';

/* ── Mega Menu: icon components (inline SVG, no extra deps) ── */
const IconSchool = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3zM5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82z" />
  </svg>
);

const IconBook = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M21 5c-1.11-.35-2.33-.5-3.5-.5-1.95 0-4.05.4-5.5 1.5-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 3.75.4 5.5 1.5 1.65-1.05 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5V6c-.6-.45-1.25-.75-2-1zm0 13.5c-1.1-.35-2.3-.5-3.5-.5-1.7 0-4.15.65-5.5 1.5V8c1.35-.85 3.8-1.5 5.5-1.5 1.2 0 2.4.15 3.5.5v11.5z" />
  </svg>
);

const IconStar = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  </svg>
);

const IconHeart = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
  </svg>
);

/* ── Mega Menu: service categories (defined outside component to avoid re-creation) ── */
const MEGA_CATEGORIES = [
  {
    id: 'education',
    heading: 'Islamic Education',
    items: [
      {
        to: '/services/madarasa',
        title: 'Madarasa',
        desc: 'Weekend Islamic school for children',
        Icon: IconSchool,
      },
      {
        to: '/services/quran-reading',
        title: 'Quran Reading',
        desc: 'Learn Tajweed & correct recitation',
        Icon: IconBook,
      },
      {
        to: '/services/quran-memorization',
        title: 'Quran Memorization',
        desc: 'Hifz program for all ages',
        Icon: IconStar,
      },
    ],
  },
  {
    id: 'community',
    heading: 'Community Services',
    items: [
      {
        to: '/services/marriage',
        title: 'Marriage Service',
        desc: 'Nikah ceremony & Islamic marriage guidance',
        Icon: IconHeart,
      },
    ],
  },
];

const Navigation = () => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navRef = useRef(null);
  const dropdownTimeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setMenuOpen(false);
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setOpenDropdown(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const isActive = (path) => location.pathname === path;

  const toggleMenu = () => setMenuOpen((prev) => !prev);

  const isMobile = () => window.innerWidth < 992;

  const handleDropdownEnter = useCallback((id) => {
    if (!isMobile()) {
      clearTimeout(dropdownTimeoutRef.current);
      setOpenDropdown(id);
    }
  }, []);

  const handleDropdownLeave = useCallback(() => {
    if (!isMobile()) {
      dropdownTimeoutRef.current = setTimeout(() => {
        setOpenDropdown(null);
      }, 200);
    }
  }, []);

  const handleDropdownToggle = (e, id) => {
    e.preventDefault();
    if (isMobile()) {
      setOpenDropdown((prev) => (prev === id ? null : id));
    }
  };

  const handleDropdownKeyDown = (e, id) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpenDropdown((prev) => (prev === id ? null : id));
    }
  };

  const servicesOpen = openDropdown === 'services';

  return (
    <nav
      ref={navRef}
      className={`navbar navbar-expand-lg navbar-dark nav-iccvt-navbar ${scrolled ? 'nav-scrolled' : ''}`}
      aria-label="Main navigation"
    >
      <div className="container px-3 px-lg-4">
        <Link
          className="navbar-brand nav-iccvt-brand d-flex align-items-center"
          to="/"
          aria-label="ICCVT Home"
        >
          <img
            src={logo}
            alt="Islamic Community Center of Vermont Logo"
            className="nav-brand-logo img-fluid"
            loading="eager"
            decoding="async"
          />
        </Link>

        <button
          className="navbar-toggler nav-iccvt-toggler border-0"
          type="button"
          onClick={toggleMenu}
          aria-controls="navbarNav"
          aria-expanded={menuOpen}
          aria-label="Toggle navigation menu"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div
          className={`collapse navbar-collapse ${menuOpen ? 'show' : ''}`}
          id="navbarNav"
        >
          <ul className="navbar-nav ms-auto align-items-lg-center">
            {/* Home */}
            <li className="nav-item">
              <Link
                className={`nav-link ${isActive('/') ? 'active' : ''}`}
                to="/"
              >
                Home
              </Link>
            </li>

            {/* About Dropdown */}
            <li
              className={`nav-item dropdown ${openDropdown === 'about' ? 'show' : ''}`}
              onMouseEnter={() => handleDropdownEnter('about')}
              onMouseLeave={handleDropdownLeave}
            >
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a
                className={`nav-link dropdown-toggle ${location.pathname.startsWith('/about') ? 'active' : ''}`}
                href="#"
                id="aboutDropdown"
                role="button"
                onClick={(e) => handleDropdownToggle(e, 'about')}
                onKeyDown={(e) => handleDropdownKeyDown(e, 'about')}
                aria-expanded={openDropdown === 'about'}
              >
                About
              </a>
              <ul className={`dropdown-menu ${openDropdown === 'about' ? 'show' : ''}`} aria-labelledby="aboutDropdown">
                <li>
                  <Link className="dropdown-item" to="/about/mission">
                    Our Mission
                  </Link>
                </li>
                <li><hr className="dropdown-divider" /></li>
                <li>
                  <Link className="dropdown-item" to="/about/board">
                    Our Board
                  </Link>
                </li>
              </ul>
            </li>

            {/* ── Services Mega Menu ─────────────────────────── */}
            <li
              className={`nav-item dropdown nav-mega-item ${servicesOpen ? 'show' : ''}`}
              onMouseEnter={() => handleDropdownEnter('services')}
              onMouseLeave={handleDropdownLeave}
            >
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a
                className={`nav-link dropdown-toggle ${location.pathname.startsWith('/services') ? 'active' : ''}`}
                href="#"
                id="servicesDropdown"
                role="button"
                aria-haspopup="true"
                aria-expanded={servicesOpen}
                onClick={(e) => handleDropdownToggle(e, 'services')}
                onKeyDown={(e) => handleDropdownKeyDown(e, 'services')}
              >
                Services
              </a>

              {/* Desktop: rich mega panel ───────────────────── */}
              <div
                className={`nav-mega-panel${servicesOpen ? ' nav-mega-panel--open' : ''}`}
                aria-label="Services submenu"
                onMouseEnter={() => handleDropdownEnter('services')}
                onMouseLeave={handleDropdownLeave}
              >
                <div className="nav-mega-inner">
                  {MEGA_CATEGORIES.map(({ id, heading, items }) => (
                    <div key={id} className="nav-mega-col">
                      <div className="nav-mega-heading">{heading}</div>
                      <ul className="nav-mega-list">
                        {items.map(({ to, title, desc, Icon }) => (
                          <li key={to}>
                            <Link
                              className={`nav-mega-link${isActive(to) ? ' nav-mega-link--active' : ''}`}
                              to={to}
                            >
                              <span className="nav-mega-icon-wrap" aria-hidden="true">
                                <Icon />
                              </span>
                              <span className="nav-mega-text">
                                <span className="nav-mega-title">{title}</span>
                                <span className="nav-mega-desc">{desc}</span>
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                {/* Footer CTA */}
                <div className="nav-mega-footer">
                  <Link className="nav-mega-footer-link" to="/services/madarasa">
                    Explore all services
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                    </svg>
                  </Link>
                </div>
              </div>

              {/* Mobile: compact stacked list ───────────────── */}
              <ul
                className={`dropdown-menu nav-mega-mobile-list${servicesOpen ? ' show' : ''}`}
                aria-labelledby="servicesDropdown"
              >
                <li>
                  <Link className="dropdown-item" to="/services/madarasa">
                    Madarasa
                  </Link>
                </li>
                <li><hr className="dropdown-divider" /></li>
                <li>
                  <Link className="dropdown-item" to="/services/quran-reading">
                    Quran Reading
                  </Link>
                </li>
                <li><hr className="dropdown-divider" /></li>
                <li>
                  <Link className="dropdown-item" to="/services/quran-memorization">
                    Quran Memorization
                  </Link>
                </li>
                <li><hr className="dropdown-divider" /></li>
                <li>
                  <Link className="dropdown-item" to="/services/marriage">
                    Marriage Service
                  </Link>
                </li>
              </ul>
            </li>
            {/* ── End Services Mega Menu ────────────────────── */}

            {/* Videos */}
            <li className="nav-item">
              <Link
                className={`nav-link ${isActive('/videos') ? 'active' : ''}`}
                to="/videos"
              >
                Videos
              </Link>
            </li>

            {/* Funeral Services */}
            <li className="nav-item">
              <Link
                className={`nav-link ${isActive('/funeral-services') ? 'active' : ''}`}
                to="/funeral-services"
              >
                Funeral Services
              </Link>
            </li>

            {/* FAQ */}
            <li className="nav-item">
              <Link
                className={`nav-link ${isActive('/faq') ? 'active' : ''}`}
                to="/faq"
              >
                FAQ
              </Link>
            </li>

            {/* Businesses Dropdown */}
            <li
              className={`nav-item dropdown ${openDropdown === 'businesses' ? 'show' : ''}`}
              onMouseEnter={() => handleDropdownEnter('businesses')}
              onMouseLeave={handleDropdownLeave}
            >
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a
                className={`nav-link dropdown-toggle ${location.pathname.startsWith('/local-businesses') ? 'active' : ''}`}
                href="#"
                id="businessesDropdown"
                role="button"
                onClick={(e) => handleDropdownToggle(e, 'businesses')}
                onKeyDown={(e) => handleDropdownKeyDown(e, 'businesses')}
                aria-expanded={openDropdown === 'businesses'}
              >
                Businesses
              </a>
              <ul className={`dropdown-menu ${openDropdown === 'businesses' ? 'show' : ''}`} aria-labelledby="businessesDropdown">
                <li>
                  <Link className="dropdown-item" to="/local-businesses">
                    Local Businesses
                  </Link>
                </li>
              </ul>
            </li>

            {/* Contact */}
            <li className="nav-item">
              <Link
                className={`nav-link ${isActive('/contact') ? 'active' : ''}`}
                to="/contact"
              >
                Contact
              </Link>
            </li>

            {/* Donate — external GoFundMe link */}
            <li className="nav-item nav-donate-item">
              <a
                className="nav-donate-btn"
                href="https://www.gofundme.com/f/15-years-in-vermont-a-new-permanent-home-for-iccvt"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Donate to ICCVT via GoFundMe"
              >
                <i className="bi bi-heart-fill nav-donate-icon" aria-hidden="true"></i>
                Donate
              </a>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
